<template>
  <div class="s-albums">
    <SButtonIcon
      v-show="showPreviousButton"
      action-type="tertiary"
      class="s-albums__nav-button s-albums__previous"
      color="gray"
      icon="fa-regular fa-chevron-left"
      opaque
      rounded
      @click="scroll('previous')"
    />
    <div ref="albumListEl" class="s-albums__carousel">
      <div v-for="album in albums" :key="album.id" class="s-albums__album">
        <button
          class="s-albums__album__image-container"
          type="button"
          @click="selectedAlbum = album.id"
        >
          <img
            :alt="album.title"
            class="s-albums__album__image"
            loading="lazy"
            :src="
              useCloudinary(album.images[0]!.path, { width: 300, height: 200 })
            "
          />
          <span v-if="album.count > 1" class="s-albums__album__count">
            <i class="fa-solid fa-images" />
            {{ album.count }}
          </span>
        </button>
        <p class="s-albums__album__title">{{ album.title }}</p>
      </div>
    </div>
    <SButtonIcon
      v-show="showNextButton"
      action-type="tertiary"
      class="s-albums__nav-button s-albums__next"
      color="gray"
      icon="fa-regular fa-chevron-right"
      opaque
      rounded
      @click="scroll('next')"
    />
    <Teleport v-if="selectedAlbum !== null" to="body">
      <lazyVueEasyLightbox
        :imgs="images"
        move-disabled
        :visible="!!selectedAlbum"
        @hide="selectedAlbum = null"
      >
        <template #toolbar />
      </lazyVueEasyLightbox>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { useScroll, useWindowSize } from "@vueuse/core";
import { computed, defineAsyncComponent, ref, useTemplateRef } from "vue";

import { SButtonIcon } from "@solvari/common-fe";
import { useCloudinary } from "@solvari/common-fe/integrations";

// Click-only → lazy; static import ships 31 kB to every page with albums.
const lazyVueEasyLightbox = defineAsyncComponent(
  () => import("vue-easy-lightbox"),
);

const mobileBreakpoint = 700;
const scrollDistances = { mobile: 248, desktop: 312 } as const;

const props = withDefaults(
  defineProps<{
    albums: App.Data.CompanyWallProjectsData[];
    showIndicators?: boolean;
  }>(),
  { showIndicators: true },
);

const albumListEl = useTemplateRef<HTMLElement>("albumListEl");

const { x: xCord, arrivedState } = useScroll(albumListEl, {
  behavior: "smooth",
});
const { width: windowWidth } = useWindowSize();

const selectedAlbum = ref<number | null>(null);

const isMobile = computed(() => windowWidth.value < mobileBreakpoint);
const images = computed<string[]>(() => {
  const album = props.albums.find((album) => album.id === selectedAlbum.value);
  if (!album) {
    return [];
  }
  return album.images.map((image) =>
    useCloudinary(image.path, { width: 1920, height: 1920 }),
  );
});
const baseScrollDistance = computed(() =>
  isMobile.value ? scrollDistances.mobile : scrollDistances.desktop,
);
const showPreviousButton = computed(() => {
  return !isMobile.value && !arrivedState.left;
});
const showNextButton = computed(() => {
  return !isMobile.value && !arrivedState.right;
});

function scroll(direction: "next" | "previous") {
  xCord.value = baseScrollDistance.value - 1;
  if (direction === "next") {
    const passedItems = Math.ceil((xCord.value + 1) / baseScrollDistance.value);
    xCord.value = baseScrollDistance.value * passedItems;
  } else {
    xCord.value = xCord.value - baseScrollDistance.value;
    const passedItems = Math.ceil(xCord.value / baseScrollDistance.value);
    xCord.value = baseScrollDistance.value * (passedItems - 1);
  }
}
</script>

<style lang="postcss">
.s-albums {
  @apply relative overflow-x-hidden;

  &__nav-button {
    @apply absolute top-1/2 z-30 -translate-y-1/2 transition-all duration-200 ease-in-out;
  }

  &__previous {
    @apply left-1;
  }

  &__next {
    @apply right-1;
  }

  &__carousel {
    @apply flex w-full flex-nowrap overflow-x-auto transition-transform duration-300 ease-out gap-6;
  }

  &__album {
    @apply relative flex w-56 flex-shrink-0 flex-col transition-transform duration-200 ease-in-out gap-2;

    @screen 700 {
      @apply w-72;
    }

    &__image-container {
      @apply relative cursor-pointer overflow-hidden rounded;

      &:hover .s-albums__album__image {
        @apply scale-105;
      }
    }

    &__image {
      @apply relative z-10 h-32 min-h-32 w-56 min-w-56 rounded bg-gray-100 object-cover transition-transform duration-300 ease-out;

      @screen 700 {
        @apply h-40 min-h-40 w-72 min-w-72;
      }
    }

    &__title {
      @apply font-semibold leading-7 text-slate-700;
    }

    &__count {
      @apply absolute bottom-0 right-0 z-20 m-2 flex h-8 max-w-fit items-center rounded-sm bg-black/80 p-2 text-sm font-medium text-white backdrop-blur-sm gap-1.5;
    }
  }
}
</style>
